import { type Product } from '@/features/shop/services/Product/types'
import { useGlobalHomeSelectStore } from '@/features/shop/components/GlobalHomePage/useGlobalHomeSelectStore'
import { type OnProductArgs } from '@/features/shop/components/ProductCard/types'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { useCallback } from 'react'
import { useRenderShelfProducts } from '@/features/shop/components/Shelf/hooks/useRenderShelfProducts'
import {
  type StoreCardForProductShelf,
  type CMSProductShelfInnerData,
} from '@/features/cms/components/CMS/types'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { screenSizes } from '@shipt/design-system-themes'
import styled from 'styled-components'
import { CarouselSection } from '@shipt/design-system-carousel'
import { Body, Headline } from '@shipt/design-system-typography'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { Box, Column, Row } from '@shipt/design-system-layouts'
import chunk from 'lodash/chunk'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { ArrowRightIcon } from '@shipt/design-system-icons'
import { RefreshShelfHeading } from '@/features/cms/components/CMS/components/Shelves/ProductShelf/ProductShelfRefresh/RefreshShelfHeading'
import { EAGER_LOAD_SHELF_INDEX } from '@/features/shared/constants/global'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { ScrollIntoView, LazyLoadObserve } from '@/features/shared/components/ScrollIntoView'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'
import { type GuestStore } from '@/features/shop/services/ShoppingStore/types'
import { useDeriveColorProps } from '@/features/cms/components/CMS/hooks/useDeriveColorProps'
import { NextLink } from '@/features/shared/elements/Link'
import { TextButtonLink, IconButtonLink } from '@/features/shared/elements/Link/Buttons'
import { logError } from '@/features/shared/utils/logger'
import { getRetailerBaseUrl } from '@shared/constants/routes'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import { OrderDetailsShelfHeader } from '@/features/shop/components/OrderDetails/OrderDetailsShelfHeader'
import { theme } from '@/features/shared/constants/shiptTheme'

type Props = {
  id: string
  index: number
  data: CMSProductShelfInnerData
  name: string
  analyticSlug?: string
  products: Product[]
  isCollection?: boolean
  categoryIndex: number | undefined
  shelfCategory: string | undefined
  adPlacementId?: string
  linkToUniversalPDP: boolean
}

const ViewAllCarouselButton = ({
  onClick,
  ariaLabel,
  href,
}: {
  onClick: () => void
  ariaLabel?: string
  href: string
}) => (
  <Column justify="center" spacing="sm" paddingLeft="sm">
    <ViewAllTextButtonLink
      href={href}
      onClick={(e) => {
        e.preventDefault()
        onClick()
      }}
      aria-label={ariaLabel}
    >
      <Column justify="center" spacing="sm">
        <Row justify="center">
          <ArrowRightIcon inherit />
        </Row>
        <div>View all</div>
      </Column>
    </ViewAllTextButtonLink>
  </Column>
)

const mapToGuestStore = (storeCard: StoreCardForProductShelf): GuestStore => {
  return {
    id: storeCard.data.retailer_id,
    metro_id: storeCard.data.metro_id,
    name: storeCard.data.name,
    store_location_id: storeCard.data.store_location_id,
    is_pickup: storeCard.data.pickup_eligible ?? false,
  }
}

export const RefreshProductShelf = ({
  id,
  index,
  data,
  analyticSlug,
  products,
  name,
  isCollection = false,
  categoryIndex,
  shelfCategory,
  adPlacementId,
  linkToUniversalPDP,
}: Props) => {
  const {
    eyebrow_text,
    web_image,
    mobile_image,
    view_all_link,
    heading,
    subheading,
    store_card,
    display_option,
    background_color,
  } = data

  const isCompact = data.header_variant === 'compact' && !!data.store_card
  const router = useSegwayRouter()
  const { trackCMSElementViewed, trackCMSElementClicked } = useCMSTrackingEvents()
  const { selectStore } = useGlobalHomeSelectStore()
  const { screenMd: isTablet } = useMediaQuery()
  const chunkSize = isTablet ? 2 : 3
  const storeName = useUserStoreName()
  const normalizedViewAllLinkUrl = getRetailerBaseUrl(
    store_card?.data.name ?? storeName,
    view_all_link?.url,
  )

  const handleViewAll = (type: 'banner' | 'link') => {
    trackCMSElementClicked({
      type,
      content: 'View all',
      // For the shelf-related CMS components, the DS team would like for us to continue sending the shelf's heading/title.
      shelf_name: heading ?? name,
      shelf_index: index,
      store_id: store_card?.data.retailer_id,
    })
    if (store_card && normalizedViewAllLinkUrl) {
      return selectStore(mapToGuestStore(store_card), normalizedViewAllLinkUrl)
    } else if (normalizedViewAllLinkUrl) {
      router.push(normalizedViewAllLinkUrl)
    } else {
      logError(
        'RefreshProductShelf: "View all" clicked but no view_all_link URL or store_card is present. The user will not be navigated anywhere.',
        {
          contentId: id,
        },
      )
    }
  }

  const handleProductClick = useCallback(
    async (args: OnProductArgs, e: React.MouseEvent) => {
      // Prevent the default behavior of the product card link since we are handling the navigation ourselves
      e.preventDefault()
      if (store_card) {
        return selectStore(
          mapToGuestStore(store_card),
          getProductPageUrl({
            id: args.product.id,
            universal_id: args.product.universal_id,
          }),
        )
      }
    },
    [selectStore, store_card],
  )

  const shelfProducts = useRenderShelfProducts({
    shelfName: heading ?? name,
    shelfIndex: index,
    shelfCategory,
    shelfCategoryIndex: categoryIndex,
    products,
    ...(store_card && { handleOnProductClick: handleProductClick }),
    trackingProps: { analyticSlug, adPlacementId },
    hasBackground: Boolean((mobile_image?.src && web_image?.src) || background_color),
    landscape: display_option === 'product_list',
    linkToUniversalPDP,
  })
  const deriveColor = useDeriveColorProps(background_color ?? 'neutral-subtle').color

  const CardCarousel = (
    <CarouselSectionCard
      $backgroundColor={deriveColor}
      data-has-bg={Boolean(background_color) || undefined}
    >
      <OrderDetailsShelfHeader />
      {isCompact ? (
        <Box padding={['lg', 'lg', 0]}>
          <RefreshShelfHeading
            index={index}
            data={data}
            handleStoreClick={() => handleViewAll('link')}
            viewAllHref={normalizedViewAllLinkUrl}
          />
        </Box>
      ) : (
        <TopContent justify="space-between">
          {!!heading && !!normalizedViewAllLinkUrl && (
            <BannerClickable
              href={normalizedViewAllLinkUrl}
              onClick={(e) => {
                e.preventDefault()
                handleViewAll('banner')
              }}
              aria-label={heading}
            />
          )}
          <Copy>
            {!!eyebrow_text && (
              <Body size="sm" uppercase variant="secondary">
                {eyebrow_text}
              </Body>
            )}
            {!!heading && (
              <Headline size="xl" as="h2">
                {heading}
              </Headline>
            )}
            {!!subheading && <Subheading>{subheading.elements.map(renderElement)}</Subheading>}
          </Copy>
          {!!web_image && !!mobile_image && (
            <ImageWrapper>
              <MobileImage
                src={mobile_image.src}
                alt={mobile_image.alt}
                fill
                sizes="174px"
                preload={index < EAGER_LOAD_SHELF_INDEX}
              />
              <DesktopImage
                src={web_image.src}
                alt={web_image.alt}
                fill
                sizes="532px"
                preload={index < EAGER_LOAD_SHELF_INDEX}
              />
            </ImageWrapper>
          )}
        </TopContent>
      )}
      {shelfProducts.length ? (
        <Carousel
          $hasBackground
          hasPagination
          key={categoryIndex}
          layoutConfig={{
            [0]: {
              // @ts-expect-error is valid attribute accepted by swiper but not currently typed as such in Astro. As follow up to experience refresh this can be addressed.
              // DS Clickup ticket: https://app.clickup.com/t/86b6q2de4
              slidesPerView: 'auto',
              slidesPerGroup: 3,
            },
          }}
        >
          {display_option === 'product_list'
            ? chunk(shelfProducts, chunkSize).map((tuple, i) => (
                <Tuple key={tuple[i]?.key}>{tuple}</Tuple>
              ))
            : shelfProducts}
          {!!normalizedViewAllLinkUrl && (
            <ViewAllCarouselButton
              ariaLabel={`view all ${heading ?? name} products`}
              onClick={() => handleViewAll('link')}
              href={normalizedViewAllLinkUrl}
            />
          )}
        </Carousel>
      ) : (
        <ErrorMessage>No products available</ErrorMessage>
      )}
    </CarouselSectionCard>
  )

  const ShelfWrapper = isCollection ? RefreshShelfContentWrapper : RefreshShelfWrapper

  return (
    <ShelfWrapper id={id}>
      <ScrollIntoView
        onInView={() => {
          trackCMSElementViewed({
            content: 'shelf',
            shelf_name: heading,
            shelf_index: index,
          })
        }}
      >
        {!!data.store_card && !isCompact && !isCollection && (
          <RefreshShelfHeading
            index={index}
            data={data}
            handleStoreClick={() => handleViewAll('link')}
            viewAllHref={normalizedViewAllLinkUrl}
          />
        )}
        <CarouselSection hasInlineControls>
          {(mobile_image && web_image) || background_color ? (
            <LazyLoadObserve minHeight={400} eagerLoad={index < EAGER_LOAD_SHELF_INDEX}>
              {CardCarousel}
            </LazyLoadObserve>
          ) : (
            <LazyLoadObserve minHeight={400} eagerLoad={index < EAGER_LOAD_SHELF_INDEX}>
              <Row justify="space-between">
                <Column>
                  {!!heading && !store_card && <Headline>{heading}</Headline>}
                  {!!subheading && !store_card && (
                    <Subheading>{subheading.elements.map(renderElement)}</Subheading>
                  )}
                </Column>
                {!!normalizedViewAllLinkUrl && !store_card && !!heading && (
                  <>
                    <Row display={{ base: 'flex', md: 'none' }} align="center">
                      <IconButtonLink
                        href={normalizedViewAllLinkUrl}
                        onClick={(e) => {
                          e.preventDefault()
                          handleViewAll('link')
                        }}
                        aria-label={`view all ${heading} products`}
                        variant="tertiary"
                        icon={ArrowRightIcon}
                        size="sm"
                      />
                    </Row>
                    <Row display={{ base: 'none', md: 'flex' }}>
                      <ViewAllTextButtonLink
                        href={normalizedViewAllLinkUrl}
                        onClick={(e) => {
                          e.preventDefault()
                          handleViewAll('link')
                        }}
                        aria-label={`view all ${heading} products`}
                      >
                        View all
                      </ViewAllTextButtonLink>
                    </Row>
                  </>
                )}
              </Row>
              <Carousel
                layoutConfig={{
                  [0]: {
                    // @ts-expect-error is valid attribute accepted by swiper but not currently typed as such in Astro. As follow up to experience refresh this can be addressed.
                    // DS Clickup ticket: https://app.clickup.com/t/86b6q2de4
                    slidesPerView: 'auto',
                    slidesPerGroup: 3,
                  },
                }}
              >
                {shelfProducts}
                {!!normalizedViewAllLinkUrl && (
                  <ViewAllCarouselButton
                    ariaLabel={`view all ${heading ?? name} products`}
                    onClick={() => handleViewAll('link')}
                    href={normalizedViewAllLinkUrl}
                  />
                )}
              </Carousel>
            </LazyLoadObserve>
          )}
        </CarouselSection>
      </ScrollIntoView>
    </ShelfWrapper>
  )
}

const RefreshShelfWrapper = styled(GridLayoutContainer)`
  margin-bottom: 1.5rem;
`

const RefreshShelfContentWrapper = styled.div`
  margin-top: 0.75rem;
  margin-bottom: 2rem;
`

const TopContent = styled(Row)`
  position: relative;
`

const Copy = styled(Column)`
  padding: 32px 0 0 16px;
  gap: 0.25rem;
`

const CarouselSectionCard = styled(CarouselSection.Card)<{
  $backgroundColor?: string
}>`
  --carousel-container-bg: ${({ $backgroundColor }) => $backgroundColor};

  &[data-has-bg] {
    background-color: var(--carousel-container-bg);
    border: 1px solid ${theme.color.border.base.neutral.subtle};
    border-radius: 16px;
  }
`

const ImageWrapper = styled.div`
  width: 100%;
  height: 7.25rem;
  max-width: 10.875rem;
  position: relative;

  @media ${screenSizes.tablet} {
    max-width: 33.188rem;
    height: 7.375rem;
  }
`

const BannerClickable = styled(NextLink)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const ViewAllTextButtonLink = styled(TextButtonLink)`
  flex-shrink: 0;
`

const Subheading = styled.div`
  z-index: 1;
  pointer-events: none;

  a,
  button {
    pointer-events: auto;
  }

  p {
    color: ${theme.color.content.base.neutral.secondary};
  }
`

const MobileImage = styled(Image)`
  border-top-right-radius: 1rem;

  @media ${screenSizes.tablet} {
    display: none;
  }
`

const DesktopImage = styled(Image)`
  display: none;
  object-fit: contain;
  border-top-right-radius: 1rem;

  @media ${screenSizes.tablet} {
    display: block;
  }
`

const Carousel = styled(CarouselSection.Carousel)<{ $hasBackground: boolean }>`
  padding: ${({ $hasBackground }) => $hasBackground && '16px 0'};

  .swiper-slide {
    width: auto;
  }
`

const Tuple = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-rows: repeat(3, 1fr);

  @media ${screenSizes.tablet} {
    grid-template-rows: 1fr 1fr;
  }
`

const ErrorMessage = styled.div`
  padding: 9.375rem 0;
  margin: 0 auto;
`
